<script setup lang="ts">
import { useFormValidation } from "../../../../../composables/useFormValidation";
import { required } from "../../../../../utils/validation";
import TextInput from "../../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import { Permission } from "../../../../../types/auth";
import { onMounted, ref } from "vue";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import GroupedSelect from "../../../../../components/ui/form/GroupedSelect.vue";
import TextAreaInput from "../../../../../components/ui/form/TextAreaInput.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import ModalFormTemplate from "../../../../../components/ui/Templates/ModalFormTemplate.vue";
import { PosStation } from "../../../../../types/models";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  parent?: any
  ignorePermission?: boolean
  posStation?: PosStation
}>();

const emit = defineEmits<{
  (e: "save", data: any[]): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  apartments_id: props.data?.apartments_id || "",
  customers_id: props.parent?.id || "",
  customer: props.parent?.name || "",
  address: props.data?.address || "",
  buildings_id: props.data?.buildings_id || "",
  primary: props.data?.primary || false,
  countrys_id: props.data?.countrys_id || 1,
  states_id: props.data?.states_id || "",
  citys_id: props.data?.citys_id || "",
  roads_id: props.data?.roads_id || "",
  lng: props.data?.lng || "",
  lat: props.data?.lat || "",
  remarks: props.data?.remarks || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("customers_id", [required("Customer is required")]);
setFieldRules("countrys_id", [required("Country is required")]);
setFieldRules("states_id", [required("State is required")]);
setFieldRules("citys_id", [required("City is required")]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + 'address-' + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
    if (props.posStation) {
      setFormData({
        countrys_id: props.posStation.outlet.countrys_id ? parseInt(props.posStation.outlet.countrys_id.toString()) : '',
        states_id: props.posStation.outlet.states_id ? parseInt(props.posStation.outlet.states_id.toString()) : '',
        citys_id: props.posStation.outlet.citys_id ? parseInt(props.posStation.outlet.citys_id.toString()) : '',
      });
      console.log(formData.value);
    }
  } else if (props.data) {
    setFormData(props.data);
  }
});
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    :ignorePermission="ignorePermission"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-3 gap-6">
        <TextInput
          v-model="fields.customer.value"
          label="Customer"
          :readonly="true"
          :error="fields.customers_id.error"
        />

        <SearchSelect
          v-model="fields.countrys_id.value"
          label="Country"
          end-point="system/master-data/countrys"
          :readonly="isReadOnly"
          :error="fields.countrys_id.touched ? fields.countrys_id.error : null"
          @blur="fields.countrys_id.touched = true"
          :allowNewRecord="true"
        />

        <GroupedSelect
          v-model="fields.states_id.value"
          label="State"
          :hasExtra="true"
          optionLabel="states"
          :extra="fields.countrys_id.value"
          end-point="system/master-data/states"
          :readonly="isReadOnly"
          :error="fields.states_id.touched ? fields.states_id.error : null"
          @blur="fields.states_id.touched = true"
          :allowNewRecord="true"
        />

        <GroupedSelect
          v-model="fields.citys_id.value"
          label="City"
          :hasExtra="true"
          :extra="fields.states_id.value"
          end-point="system/master-data/citys"
          :readonly="isReadOnly"
          :error="fields.citys_id.touched ? fields.citys_id.error : null"
          optionLabel="citys"
          @blur="fields.citys_id.touched = true"
          :allowNewRecord="true"
        />

        <GroupedSelect
          v-model="fields.roads_id.value"
          label="Road"
          optionLabel="roads"
          :hasExtra="true"
          :extra="fields.citys_id.value"
          end-point="system/master-data/roads"
          :readonly="isReadOnly"
          :error="fields.roads_id.touched ? fields.roads_id.error : null"
          @blur="fields.roads_id.touched = true"
          :allowNewRecord="true"
        />

        <GroupedSelect
          v-model="fields.buildings_id.value"
          label="Building"
          :hasExtra="true"
          optionLabel="buildings"
          :remote="true"
          :extra="fields.citys_id.value"
          end-point="system/master-data/buildings"
          :readonly="isReadOnly"
          :error="
            fields.buildings_id.touched ? fields.buildings_id.error : null
          "
          @blur="fields.buildings_id.touched = true"
          :allowNewRecord="true"
        />

        <TextAreaInput
          v-model="fields.address.value"
          label="Address"
          :rows="3"
          :readonly="isReadOnly"
          :error="fields.address.touched ? fields.address.error : null"
          @blur="fields.address.touched = true"
        />

        <NumberInput
          v-model="fields.lat.value"
          label="Latitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lat.touched ? fields.lat.error : null"
          @blur="fields.lat.touched = true"
        />

        <NumberInput
          v-model="fields.lng.value"
          label="Longitude"
          :readonly="isReadOnly"
          :min="0"
          :error="fields.lng.touched ? fields.lng.error : null"
          @blur="fields.lng.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
